import { CalendarIcon, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { fr, enUS, es } from "date-fns/locale";

import { Calendar } from "@stockline/ui/components/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@stockline/ui/components/popover";
import { cn } from "@/lib/utils";

interface DateFieldButtonProps {
  value: Date | null;
  onChange: (date: Date | null) => void;
  disabled?: boolean;
  /** Tighter sizing for inline table rows. */
  compact?: boolean;
  /** Greyed label shown when no date is set (e.g. the field's name). */
  placeholder?: string;
  "data-testid"?: string;
}

/**
 * Clean single-date control: a trigger button (calendar icon + formatted day)
 * that opens a popover calendar. Picking a day sets the value and closes —
 * no Apply step. Mirrors the order/delivery date trigger styling so custom
 * date fields match the rest of the recap. Value is a plain local-field `Date`
 * (the caller round-trips it to/from a bare `YYYY-MM-DD` calendar day).
 */
export function DateFieldButton({
  value,
  onChange,
  disabled = false,
  compact = false,
  placeholder,
  "data-testid": testId,
}: Readonly<DateFieldButtonProps>) {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);

  // Match region variants too (fr-FR, es-ES, …) — not just "fr"/"es".
  const calendarLocale = (() => {
    const lang = i18n.language.split("-")[0].toLowerCase();
    if (lang === "es") return es;
    if (lang === "fr") return fr;
    return enUS;
  })();

  // value is local-midnight, so toLocaleDateString reads back the right day
  // (no timezone drift).
  const formatted = value
    ? value.toLocaleDateString(i18n.language || "fr")
    : null;

  // A set value is clearable (unless disabled) so the field can return to
  // empty — ERP export then falls back to the derived J-1/J-2 prep date.
  // Rendered as a sibling overlay, not nested in the trigger button.
  const showClear = Boolean(value) && !disabled;

  return (
    <div className="relative w-full">
      <Popover
        open={disabled ? false : open}
        onOpenChange={disabled ? undefined : setOpen}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid={testId}
            disabled={disabled}
            className={cn(
              "flex items-center gap-2 px-2.5 rounded-md border border-gray-200 bg-gray-50/80 hover:border-gray-300 hover:bg-white transition-colors text-left w-full overflow-hidden",
              "disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-gray-50/80 disabled:hover:border-gray-200",
              compact ? "h-7 text-xs" : "h-8 text-sm",
              showClear && "pr-7",
            )}
          >
            <CalendarIcon className="h-3.5 w-3.5 text-gray-600 flex-shrink-0" />
            {formatted ? (
              <span className="font-medium text-gray-700 truncate">
                {formatted}
              </span>
            ) : (
              <span className="text-gray-400 truncate">{placeholder}</span>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="w-auto p-0"
          align="start"
          onClick={(e) => e.stopPropagation()}
        >
          <Calendar
            mode="single"
            locale={calendarLocale}
            defaultMonth={value ?? undefined}
            selected={value ?? undefined}
            onSelect={(date) => {
              if (date) {
                onChange(date);
                setOpen(false);
              }
            }}
          />
        </PopoverContent>
      </Popover>
      {showClear && (
        <button
          type="button"
          data-testid={testId ? `${testId}-clear` : undefined}
          aria-label={t("common.clear", "Clear")}
          onClick={() => onChange(null)}
          className="absolute right-1.5 top-1/2 -translate-y-1/2 flex items-center justify-center rounded-sm p-0.5 text-gray-400 hover:text-gray-600 hover:bg-gray-200/70 transition-colors"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}
